'use client'

import {useEffect, useRef, useState, type ImgHTMLAttributes} from 'react'
import {avatarDisplaySrc, avatarFor, isLocalAvatarUrl} from '@/lib/utils'

type LazyAvatarProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'loading' | 'decoding'> & {
    /**
     * Remote avatar URL (usually from `contact.avatar`). When empty / nullish
     * the component shows only the SVG placeholder keyed off `seed`.
     */
    src?: string | null
    /**
     * Fallback seed used to generate the inline SVG placeholder via
     * `avatarFor`. Typically the wxid. Falls back to `anonymous`.
     */
    seed: string
    /**
     * How many px before the viewport to start loading. Default 200 keeps
     * scrolling smooth without over-fetching.
     */
    rootMargin?: string
}

/**
 * Avatar that defers fetching the local cached image until it scrolls into view.
 *
 * Behavior:
 *   - Renders the local SVG placeholder from `avatarFor(seed)` immediately.
 *   - Remote http(s) URLs are never loaded here. They are shown only after a
 *     deliberate avatar click in the profile popover.
 *   - Once an `IntersectionObserver` reports the element near the viewport
 *     (default 200px buffer), swaps in the cached `/api/avatars/...` src.
 *   - If the network image fails to load (404, broken CDN, …), reverts to
 *     the placeholder so the UI never shows a broken-image icon.
 *   - Disconnects the observer the moment loading begins — we only need to
 *     trigger once per mount.
 *
 * Also sets the native `loading="lazy"` + `decoding="async"` as a belt-and-
 * suspenders fallback for any browser that preempts the observer.
 *
 * Cost profile for a 451-row list: exactly 0 network requests on first
 * render; O(visible rows) requests as the user scrolls.
 */
export function LazyAvatar({
    src,
    seed,
    rootMargin = '200px',
    alt = '',
    ...rest
}: LazyAvatarProps) {
    const placeholder = avatarFor(seed)
    const [resolvedSrc, setResolvedSrc] = useState<string>(placeholder)
    const imgRef = useRef<HTMLImageElement | null>(null)

    useEffect(() => {
        // Only cached/local avatars may be loaded automatically. Remote URLs
        // stay behind the deterministic placeholder until the user clicks.
        if (!isLocalAvatarUrl(src)) {
            setResolvedSrc(placeholder)
            return
        }
        const cachedSrc = avatarDisplaySrc(src, seed)

        const node = imgRef.current
        if (!node) {
            setResolvedSrc(cachedSrc)
            return
        }

        // Safety: IntersectionObserver isn't available in JSDOM or very old
        // browsers. Fall back to immediate load.
        if (typeof IntersectionObserver === 'undefined') {
            setResolvedSrc(cachedSrc)
            return
        }

        // If already showing a real image (not placeholder), update directly
        // to avoid flash when contact data arrives.
        if (resolvedSrc !== placeholder) {
            setResolvedSrc(cachedSrc)
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setResolvedSrc(cachedSrc)
                        observer.disconnect()
                        break
                    }
                }
            },
            {rootMargin},
        )
        observer.observe(node)
        return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, seed, placeholder, rootMargin])

    return (
        <img
            ref={imgRef}
            src={resolvedSrc}
            alt={alt}
            loading="lazy"
            decoding="async"
            onError={() => {
                // Fall back to the local SVG placeholder if the remote
                // image fails. Avoids the browser's default broken-image
                // icon and keeps the grid looking clean.
                if (resolvedSrc !== placeholder) setResolvedSrc(placeholder)
            }}
            {...rest}
        />
    )
}
